<template>
  <div>
    <v-dialog v-model="show" persistent max-width="90%">
      <v-card>
        <v-app-bar dense dark text color="secondary">
          <v-toolbar-title class="subheading">{{
            title || $t("courses.course_popup_title", { length: number_of_enrolees })
          }}</v-toolbar-title>

          <v-spacer></v-spacer>

          <v-form ref="form" @submit.prevent="filter" class="mt-8 d-none d-sm-flex">
            <v-container grid-list-md>
              <v-layout>
                <v-flex xs12>
                  <v-text-field
                    :label="$t('global.search')"
                    v-model="search_params.any"
                    @keydown="checkKeyPress"
                    hide-details=""
                    autocomplete="new-password"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 v-if="show_course_filter">
                  <v-select
                    v-model="search_params.id"
                    :items="courses_filter"
                    item-text="name"
                    item-value="id"
                    :label="$t('courses.filter_course')"
                    @change="filter"
                    clearable
                  >
                  </v-select>
                </v-flex>
                <v-flex xs12 v-if="show_coursetype_filter">
                  <v-select
                    v-model="search_params.coursetype_id"
                    :items="coursetypes_filter"
                    item-text="fullname"
                    item-value="id"
                    :label="$t('coursetypes.title')"
                    @change="filter"
                    clearable
                  >
                  </v-select>
                </v-flex>
                <v-flex xs12>
                  <v-select
                    v-model="selected_status_ids"
                    :items="course_status"
                    item-text="text"
                    item-value="key"
                    :label="$t('courses.status')"
                    @change="filter"
                    hide-details
                    multiple
                  >
                    <template v-slot:selection="{ item, index }">
                      <span v-if="index === 0">{{ item.text }}</span>
                      <span v-if="index === 1" class="grey--text caption">&nbsp;(+{{ selected_status_ids.length - 1 }} others)</span>
                    </template>
                  </v-select>
                </v-flex>
                <v-flex xs12 v-if="from_module == 'companies'">
                  <v-checkbox
                    :label="$t('courses.company_internal_course')"
                    v-model="company_internal_course"
                    @change="filter"
                    dense
                    hide-details
                    class="chk-cic"
                  />
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>

          <action-button type="save" :text="$t('enrolments.enrol')" @click="submit" small />
          <action-button type="cancel" @click="cancel" small />
        </v-app-bar>

        <v-divider></v-divider>

        <v-card-text class="py-0 mt-4">
          <v-data-table
            :headers="headers"
            :items="courses"
            :items-per-page="courses.length"
            :options.sync="pagination"
            :footer-props="{
              showFirstLastPage: true,
              'items-per-page-options': this.DATATABLE_ITEM_PER_PAGE_OPTIONS,
              'items-per-page-all-text': $t('global.items_per_page_all_text'),
              'items-per-page-text': $t('global.items_per_page_text'),
              'page-text': $t('global.page_text')
            }"
            :server-items-length="total"
            :loading="show_loading"
            :loading-text="$t('global.loading_text')"
            :no-data-text="$t('global.no_data_text')"
            :no-results-text="$t('global.no_results_text')"
            item-key="id"
            :show-select="true"
            :single-select="single_select"
            v-model="selected"
            @toggle-select-all="toggleSelectAll($event)"
            :must-sort="true"
          >
            <template v-slot:header.data-table-select="{ props, on }">
              <v-simple-checkbox v-on="on" v-bind="props" />
              <span class="dt-selectall-count">({{ selectAll ? total : selected.length }})</span>
            </template>

            <template v-slot:item.data-table-select="{ isSelected, select, item }">
              <v-simple-checkbox
                class="d-inline"
                :value="isSelected && (item.coursetemplate.type == 1 ? number_of_enrolees <= item.num_places - item.num_enrolled : true)"
                @input="select($event)"
                :disabled="item.coursetemplate.type == 1 ? number_of_enrolees > item.num_places - item.num_enrolled : false"
              ></v-simple-checkbox>
            </template>

            <template v-slot:item.category="{ item }">
              {{ item.coursetype.category }}
            </template>

            <template v-slot:item.address="{ item }">
              {{ item.address.city }}
            </template>

            <template v-slot:item.instructor="{ item }">
              {{ item.instructor.fullname }}
            </template>

            <template v-slot:item.start_datetime="{ item }">
              {{
                item.start_date
                  ? moment.utc(item.start_date).format(DATE_FORMAT) + " " + moment.utc(item.start_time, TIME_FORMAT).format(TIME_FORMAT)
                  : null
              }}
            </template>

            <template v-slot:item.end_datetime="{ item }">
              {{
                item.end_date
                  ? moment.utc(item.end_date).format(DATE_FORMAT) + " " + moment.utc(item.end_time, TIME_FORMAT).format(TIME_FORMAT)
                  : null
              }}
            </template>

            <template v-slot:item.status="{ item }">
              {{ $dd_course_status(item.status) }}
            </template>

            <template v-slot:item.is_published="{ item }">
              {{ item.is_published ? $t("global.yes") : $t("global.no") }}
            </template>

            <template v-slot:item.action="{ item }">
              <no-available-seats
                :show="item.coursetemplate.type == 1 ? number_of_enrolees > item.num_places - item.num_enrolled : false"
              ></no-available-seats>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "CoursesListPopup",

  props: {
    show: {
      type: Boolean,
      required: true
    },
    title: {
      type: String
    },
    from_id: {
      type: Number
    },
    from_module: {
      type: String
    },
    single_select: {
      type: Boolean,
      default: false
    },
    except_ids: {
      type: Array,
      default: Array
    },
    status_ids: {
      type: Array,
      default: Array
    },
    number_of_enrolees: {
      type: Number,
      default: 0
    },
    coursetype_id: {
      type: Number
    },
    coursetype_ids: {
      type: Array,
      default: Array
    },
    show_course_filter: {
      type: Boolean,
      default: true
    },
    show_coursetype_filter: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      url: "",
      selected: [],
      selectAll: false,

      courses: [],
      courses_filter: [],
      coursetypes_filter: [],
      selected_status_ids: [],

      course_status: [],
      company_internal_course: null,

      total: 0,
      all_ids: [],
      show_loading: false,
      show_filter: false,
      refresh: false,
      pagination: {
        itemsPerPage: this.$getSetting("rows_per_page"),
        sortBy: ["start_datetime"],
        sortDesc: []
      },
      search_params: {
        not_in_ids: "",
        title: "",
        course_id: null,
        status_ids: "",
        company_id: null,
      },
      firstLoad: true
    }
  },

  computed: {
    headers: {
      get() {
        let headers = [
          { text: "", value: "action", sortable: false, width: "20px" },
          { text: this.$t("courses.name"), value: "name" },
          { text: this.$t("coursetypes.short_title"), value: "category", sortable: false },
          { text: this.$t("courses.location"), value: "address" },
          { text: this.$t("courses.instructor_id"), value: "instructor", sortable: false },
          { text: this.$t("courses.start_datetime"), value: "start_datetime" },
          { text: this.$t("courses.end_datetime"), value: "end_datetime" },
          { text: this.$t("courses.num_places"), value: "num_places" },
          { text: this.$t("courses.num_enrolled"), value: "num_enrolled", sortable: false },
          { text: this.$t("courses.status"), value: "status", sortable: false },
          { text: this.$t("courses.is_published"), value: "is_published", sortable: false }
        ]

        if (this.$hasRole("company", true)) {
          headers = [
            { text: "", value: "action", sortable: false, width: "20px" },
            { text: this.$t("courses.name"), value: "name" },
            { text: this.$t("courses.location"), value: "address" },
            { text: this.$t("courses.instructor_id"), value: "instructor", sortable: false },
            { text: this.$t("courses.start_datetime"), value: "start_datetime" },
            { text: this.$t("courses.end_datetime"), value: "end_datetime" },
            { text: this.$t("courses.num_places"), value: "num_places" }
          ]
        }

        return headers
      }
    }
  },

  watch: {
    refresh: {
      handler() {
        if (this.refresh) {
          this.list()
        }
      }
    },
    pagination: {
      handler(newVal, oldVal) {
        if (!_.isEqual(newVal, oldVal)) this.refresh = true
      }
    },
    show: {
      handler(val) {
        if (val) {
          this.selected = []
          this.courses = []
          this.list()
        }
      }
    }
  },

  mounted() {
    this.url = this.API_URL + "courses"

    let course_status = this.$dd_course_status()
    course_status = course_status.filter((i) => i.key == 1 || i.key == 5)

    this.course_status = course_status
  },

  methods: {
    toggleSelectAll(event) {
      this.selectAll = event.value
      if (this.selectAll) {
        this.selected = []
        this.all_ids.forEach((id) => {
          this.selected.push({ id: id })
        })
      } else {
        this.selected = []
      }
    },

    list() {
      this.show_loading = true

      if (this.search_params.id) {
        this.$set(this.search_params, "not_in_ids", "")
      }
      this.$set(this.search_params, "not_in_ids", this.except_ids ? this.except_ids.join(",") : "")
      this.$set(this.search_params, "coursetype_ids", this.coursetype_ids ? this.coursetype_ids.join(",") : "")

      if (this.firstLoad) {
        this.$set(this.search_params, "coursetype_id", this.coursetype_id ? this.coursetype_id : "")
        if (this.status_ids) {
          this.selected_status_ids = this.status_ids
        }
        this.firstLoad = false
      }

      if (this.selected_status_ids) {
        this.$set(this.search_params, "statuses", this.selected_status_ids ? this.selected_status_ids.join(",") : "")
      }

      if (this.$hasRole("company", true)) {
        this.$set(this.search_params, "is_published", 1)
      }

      let { sortBy, descending, page, itemsPerPage } = this.pagination
      let query = this.$queryString(this.pagination, this.search_params)

      axios.get(this.url + "?resourcetype=course-basic-list&" + query).then((res) => {
        this.courses = res.data.data

        if (this.courses_filter.length == 0) {
          this.courses_filter = JSON.parse(JSON.stringify(this.courses))
        }

        this.total = res.data.meta.total
        this.refresh = false
        this.show_loading = false
        this.all_ids = res.data.meta.all_ids

        this.pagination.page = res.data.meta.current_page
        this.pagination.itemsPerPage = Number(res.data.meta.per_page)
      })

      this.ddlist_filter()
    },

    ddlist_filter() {
      axios.post(this.API_URL + "request/ddlists", [{ key: "coursetypes", module: "coursetypes", query: "show_all=true" }]).then((res) => {
        this.coursetypes_filter = res.data.coursetypes
      })
    },
    filter(target) {
      this.search_params.company_id = null
      if(this.company_internal_course) {
        this.search_params.company_id = this.from_id
      }

      this.refresh = true
    },

    checkKeyPress(e) {
      if (e.key == "Enter") {
        this.filter()
      }
    },

    reset() {
      this.$refs.form.reset()
      this.toggleSelectAll(false)
      this.refresh = true
    },

    cancel() {
      this.firstLoad = true
      this.$emit("callback_courselistpopup_cancel")
      this.reset()
    },

    submit() {
      let vm = this

      this.firstLoad = true

      if (this.selected.length == 0) {
        this.$awn.alert(vm.$t("courses.no_course_selected"))
      } else {
        this.$emit("callback_courselistpopup_submit", this.selected)
      }

      this.reset()
    }
  }
}
</script>

<style>
.chk-cic.v-input--checkbox .v-label {
  color: #fff !important;
}
</style>
